import React from "react";

/**
 * TableShell — Warm Precision design-system primitive for data tables.
 *
 * Use for: any tabular data list (users, contracts, integrations, logs). The
 * shell handles the card-shaped border, rounded corners, overflow scroll, and
 * sticky-header positioning so pages don't reinvent table chrome each time.
 *
 * Pattern:
 *   <TableShell stickyHeader>
 *     <TableShell.Header>
 *       <TableShell.HeaderCell sortable sortKey="name" sortDir={dir} onSort={setSort}>Name</TableShell.HeaderCell>
 *       <TableShell.HeaderCell>Status</TableShell.HeaderCell>
 *       <TableShell.HeaderCell align="right" numeric>Updated</TableShell.HeaderCell>
 *     </TableShell.Header>
 *     <TableShell.Body>
 *       {rows.map(r => (
 *         <TableShell.Row key={r.id} onClick={() => open(r.id)}>
 *           <TableShell.Cell>{r.name}</TableShell.Cell>
 *           <TableShell.Cell>{r.status}</TableShell.Cell>
 *           <TableShell.Cell align="right" numeric>{r.updated}</TableShell.Cell>
 *         </TableShell.Row>
 *       ))}
 *     </TableShell.Body>
 *   </TableShell>
 *
 * Numeric columns get `tabular-nums` automatically when `numeric` is set.
 */

type Align = "left" | "right" | "center";

interface TableShellProps {
  stickyHeader?: boolean;
  className?: string;
  children?: React.ReactNode;
  /** Optional aria-label for screen readers. */
  "aria-label"?: string;
}

function TableShell({ stickyHeader, className = "", children, ...rest }: TableShellProps) {
  return (
    <div className={`rounded-lg border border-zinc-200 bg-white overflow-x-auto ${className}`}>
      <table
        className={`w-full text-sm text-zinc-800 ${stickyHeader ? "ts-sticky" : ""}`}
        {...rest}
      >
        {children}
      </table>
    </div>
  );
}

function Header({ children }: { children?: React.ReactNode }) {
  return (
    <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wide border-b border-zinc-200 [.ts-sticky_&]:sticky [.ts-sticky_&]:top-0 [.ts-sticky_&]:z-[1]">
      <tr>{children}</tr>
    </thead>
  );
}

interface HeaderCellProps {
  sortable?: boolean;
  sortKey?: string;
  sortDir?: "asc" | "desc" | null;
  /** Called with the column's sortKey when clicked. */
  onSort?: (key: string) => void;
  align?: Align;
  numeric?: boolean;
  className?: string;
  children?: React.ReactNode;
}

function HeaderCell({
  sortable,
  sortKey,
  sortDir,
  onSort,
  align = "left",
  numeric,
  className = "",
  children,
}: HeaderCellProps) {
  const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  const numCls = numeric ? "tabular-nums" : "";
  const ariaSort =
    sortable && sortKey
      ? sortDir === "asc"
        ? "ascending"
        : sortDir === "desc"
        ? "descending"
        : "none"
      : undefined;
  const handleClick = () => {
    if (sortable && sortKey && onSort) onSort(sortKey);
  };
  const indicator = !sortable
    ? null
    : sortDir === "asc"
    ? " ▲"
    : sortDir === "desc"
    ? " ▼"
    : "";
  if (sortable) {
    return (
      <th
        scope="col"
        aria-sort={ariaSort}
        className={`px-[var(--ds-table-cell-px)] py-[var(--ds-table-cell-py)] font-semibold ${alignCls} ${numCls} ${className}`}
      >
        <button
          type="button"
          onClick={handleClick}
          className="inline-flex items-center gap-1 uppercase tracking-wide text-xs font-semibold text-zinc-500 hover:text-zinc-800 focus:outline-none focus-visible:underline"
        >
          {children}
          <span aria-hidden="true">{indicator}</span>
        </button>
      </th>
    );
  }
  return (
    <th
      scope="col"
      className={`px-[var(--ds-table-cell-px)] py-[var(--ds-table-cell-py)] font-semibold ${alignCls} ${numCls} ${className}`}
    >
      {children}
    </th>
  );
}

function Body({ children, className = "" }: { children?: React.ReactNode; className?: string }) {
  return <tbody className={`divide-y divide-zinc-100 ${className}`}>{children}</tbody>;
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
  children?: React.ReactNode;
}

function Row({ onClick, selected, className = "", children, ...rest }: RowProps) {
  const interactive = !!onClick;
  return (
    <tr
      onClick={onClick}
      className={`${selected ? "bg-zinc-50" : ""} ${
        interactive ? "cursor-pointer hover:bg-zinc-50" : ""
      } ${className}`}
      {...rest}
    >
      {children}
    </tr>
  );
}

interface CellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  align?: Align;
  numeric?: boolean;
  children?: React.ReactNode;
}

function Cell({ align = "left", numeric, className = "", children, ...rest }: CellProps) {
  const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  const numCls = numeric ? "tabular-nums" : "";
  return (
    <td
      className={`font-data px-[var(--ds-table-cell-px)] py-[var(--ds-table-cell-py)] ${alignCls} ${numCls} ${className}`}
      {...rest}
    >
      {children}
    </td>
  );
}

function Empty({
  message = "Nothing to show",
  children,
  colSpan,
}: {
  message?: string;
  children?: React.ReactNode;
  colSpan?: number;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-zinc-400">
        {children ?? message}
      </td>
    </tr>
  );
}

TableShell.Header = Header;
TableShell.HeaderCell = HeaderCell;
TableShell.Body = Body;
TableShell.Row = Row;
TableShell.Cell = Cell;
TableShell.Empty = Empty;

export default TableShell;
